import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { excavatorsAPI, customersAPI, exportAPI, importAPI } from "../lib/api";
import { compressImage } from "../lib/imageUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Truck, Hash, Calendar, User, Edit, Trash2, Download, FileText, Shield, X, Eye, Upload, FileSpreadsheet, Camera } from "lucide-react";

const Excavators = () => {
  const { user } = useAuth();
  const [excavators, setExcavators] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingExcavator, setEditingExcavator] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewPhotoDialog, setViewPhotoDialog] = useState({ open: false, photo: null, title: "" });
  const [formData, setFormData] = useState({
    model: "",
    serial_number: "",
    customer_id: "",
    year: "",
    notes: "",
    lm_cert_photo: "",
    insurance_cert_photo: "",
    photos: [],
  });

  const canManage = user?.role === "admin" || user?.role === "manager";
  const canExport = user?.role === "admin" || user?.role === "manager";
  const canImport = user?.role === "admin" || user?.role === "manager";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [excavatorsRes, customersRes] = await Promise.all([
        excavatorsAPI.getAll(),
        customersAPI.getAll(),
      ]);
      setExcavators(excavatorsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      model: "",
      serial_number: "",
      customer_id: "",
      year: "",
      notes: "",
      lm_cert_photo: "",
      insurance_cert_photo: "",
      photos: [],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      model: formData.model,
      serial_number: formData.serial_number,
      year: formData.year ? parseInt(formData.year) : null,
      customer_id: formData.customer_id || null,
      notes: formData.notes || null,
      lm_cert_photo: formData.lm_cert_photo || null,
      insurance_cert_photo: formData.insurance_cert_photo || null,
      photos: formData.photos || [],
    };
    try {
      if (editingExcavator) {
        await excavatorsAPI.update(editingExcavator.id, submitData);
        toast.success("Excavator updated");
      } else {
        await excavatorsAPI.create(submitData);
        toast.success("Excavator added");
      }
      setDialogOpen(false);
      setEditingExcavator(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (excavator) => {
    setEditingExcavator(excavator);
    setFormData({
      model: excavator.model,
      serial_number: excavator.serial_number,
      customer_id: excavator.customer_id || "",
      year: excavator.year?.toString() || "",
      notes: excavator.notes || "",
      lm_cert_photo: excavator.lm_cert_photo || "",
      insurance_cert_photo: excavator.insurance_cert_photo || "",
      photos: excavator.photos || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (excavatorId) => {
    try {
      await excavatorsAPI.delete(excavatorId);
      toast.success("Excavator deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete excavator");
    }
  };

  const handleExport = async () => {
    try {
      toast.info("Preparing export...");
      await exportAPI.download("excavators");
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      toast.info("Downloading template...");
      await importAPI.downloadTemplate("excavators");
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const response = await importAPI.upload("excavators", file);
      const { imported, skipped, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`Imported ${imported} excavators successfully`);
      }
      if (skipped > 0) {
        toast.warning(`Skipped ${skipped} rows`);
      }
      if (errors && errors.length > 0) {
        errors.slice(0, 3).forEach(err => toast.error(err));
      }
      
      setImportDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const handlePhotoUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Check if it's a PDF file
      if (file.type === 'application/pdf') {
        // Convert PDF to base64 directly (no compression)
        const reader = new FileReader();
        reader.onload = () => {
          setFormData((prev) => ({ ...prev, [field]: reader.result }));
          toast.success("PDF uploaded");
        };
        reader.readAsDataURL(file);
      } else {
        // Compress image as before
        const compressed = await compressImage(file, 400);
        setFormData((prev) => ({ ...prev, [field]: compressed }));
        toast.success("Photo uploaded");
      }
    } catch (error) {
      toast.error("Failed to upload file");
    }
  };

  const handleGeneralPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file, 400);
      setFormData((prev) => ({ 
        ...prev, 
        photos: [...(prev.photos || []), compressed]
      }));
      toast.success("Photo added");
    } catch (error) {
      toast.error("Failed to upload photo");
    }
    e.target.value = "";
  };

  const handleRemoveGeneralPhoto = (index) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleRemovePhoto = (field) => {
    setFormData((prev) => ({ ...prev, [field]: "" }));
  };

  const openPhotoViewer = (photo, title) => {
    setViewPhotoDialog({ open: true, photo, title });
  };

  // Helper function to view PDF in new tab
  const viewPdf = (base64Data) => {
    // Convert base64 to blob and open in new tab
    const byteCharacters = atob(base64Data.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };

  // Helper function to download PDF
  const downloadPdf = (base64Data, filename) => {
    const byteCharacters = atob(base64Data.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to download image
  const downloadImage = (base64Data, filename) => {
    const link = document.createElement('a');
    link.href = base64Data;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredExcavators = excavators.filter(
    (e) =>
      e.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="excavators-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Excavators
          </h1>
          <p className="text-slate-500">{filteredExcavators.length} units registered</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canExport && (
            <Button variant="outline" onClick={handleExport} className="rounded-sm" data-testid="export-excavators-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          {canImport && (
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="import-excavators-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-bold uppercase">
                    Import Excavators
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-sm border border-slate-200">
                    <h4 className="font-semibold text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      Step 1: Download Template
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Download the Excel template with the correct column format.
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleDownloadTemplate}
                      className="w-full rounded-sm"
                      data-testid="download-excavators-template-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-sm border border-slate-200">
                    <h4 className="font-semibold text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Step 2: Upload Filled Template
                    </h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Fill in your excavator data and upload the Excel file.
                    </p>
                    <label className="block">
                      <Input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleImport}
                        disabled={importing}
                        className="rounded-sm cursor-pointer"
                        data-testid="excavators-import-file"
                      />
                    </label>
                    {importing && (
                      <p className="text-sm text-amber-600 mt-2 flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                        Importing...
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 space-y-1">
                    <p><strong>Required columns:</strong> model, serial_number</p>
                    <p><strong>Optional columns:</strong> year, customer_name, notes</p>
                    <p>Customer names are matched to existing customers. Duplicate serial numbers will be skipped.</p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingExcavator(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-blue-500 text-black font-bold uppercase tracking-wider hover:bg-blue-600 rounded-sm border-2 border-transparent hover:border-black"
                  data-testid="add-excavator-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Excavator
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl font-bold uppercase">
                    {editingExcavator ? "Edit Excavator" : "Add Excavator"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">Model *</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="e.g., CAT 320D, Komatsu PC200"
                      className="rounded-sm"
                      required
                      data-testid="excavator-model"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">Serial Number *</Label>
                    <Input
                      value={formData.serial_number}
                      onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      placeholder="e.g., CAT0320D12345"
                      className="rounded-sm"
                      required
                      data-testid="excavator-serial"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide">Year</Label>
                      <Input
                        type="number"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        placeholder="2020"
                        className="rounded-sm"
                        data-testid="excavator-year"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-semibold uppercase text-xs tracking-wide">Owner</Label>
                      <Select
                        value={formData.customer_id}
                        onValueChange={(v) => setFormData({ ...formData, customer_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="excavator-owner">
                          <SelectValue placeholder="Select owner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No owner</SelectItem>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* LM Certificate Photo */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      LM Certificate (Optional)
                    </Label>
                    {formData.lm_cert_photo ? (
                      <div className="relative">
                        {formData.lm_cert_photo.startsWith('data:application/pdf') ? (
                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
                            <div className="flex items-center gap-2">
                              <FileText className="w-8 h-8 text-red-500" />
                              <span className="text-sm text-slate-600 font-medium">LM Certificate.pdf</span>
                            </div>
                            <div className="flex gap-1 ml-auto">
                              <button
                                type="button"
                                onClick={() => viewPdf(formData.lm_cert_photo)}
                                className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600"
                                title="View PDF"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => downloadPdf(formData.lm_cert_photo, 'LM_Certificate.pdf')}
                                className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600"
                                title="Download PDF"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemovePhoto("lm_cert_photo")}
                                className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                                title="Remove"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="relative inline-block">
                            <img
                              src={formData.lm_cert_photo}
                              alt="LM Certificate"
                              className="w-32 h-24 object-cover rounded-sm border border-slate-200 cursor-pointer hover:border-blue-400"
                              onClick={() => openPhotoViewer(formData.lm_cert_photo, "LM Certificate")}
                            />
                            <div className="absolute -top-2 -right-2 flex gap-1">
                              <button
                                type="button"
                                onClick={() => downloadImage(formData.lm_cert_photo, 'LM_Certificate.png')}
                                className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600"
                                title="Download"
                              >
                                <Download className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemovePhoto("lm_cert_photo")}
                                className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                                title="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handlePhotoUpload(e, "lm_cert_photo")}
                          className="hidden"
                          data-testid="lm-cert-upload"
                        />
                        <div className="text-center">
                          <FileText className="w-8 h-8 text-slate-400 mx-auto mb-1" />
                          <span className="text-sm text-slate-500">Upload LM Cert (Image/PDF)</span>
                        </div>
                      </label>
                    )}
                  </div>

                  {/* Insurance Certificate Photo */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      Insurance Certificate (Optional)
                    </Label>
                    {formData.insurance_cert_photo ? (
                      <div className="relative">
                        {formData.insurance_cert_photo.startsWith('data:application/pdf') ? (
                          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
                            <div className="flex items-center gap-2">
                              <FileText className="w-8 h-8 text-red-500" />
                              <span className="text-sm text-slate-600 font-medium">Insurance Certificate.pdf</span>
                            </div>
                            <div className="flex gap-1 ml-auto">
                              <button
                                type="button"
                                onClick={() => viewPdf(formData.insurance_cert_photo)}
                                className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600"
                                title="View PDF"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => downloadPdf(formData.insurance_cert_photo, 'Insurance_Certificate.pdf')}
                                className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600"
                                title="Download PDF"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemovePhoto("insurance_cert_photo")}
                                className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                                title="Remove"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="relative inline-block">
                            <img
                              src={formData.insurance_cert_photo}
                              alt="Insurance Certificate"
                              className="w-32 h-24 object-cover rounded-sm border border-slate-200 cursor-pointer hover:border-green-400"
                              onClick={() => openPhotoViewer(formData.insurance_cert_photo, "Insurance Certificate")}
                            />
                            <div className="absolute -top-2 -right-2 flex gap-1">
                              <button
                                type="button"
                                onClick={() => downloadImage(formData.insurance_cert_photo, 'Insurance_Certificate.png')}
                                className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600"
                                title="Download"
                              >
                                <Download className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemovePhoto("insurance_cert_photo")}
                                className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                                title="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-green-400 hover:bg-green-50/50 transition-colors">
                        <Input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => handlePhotoUpload(e, "insurance_cert_photo")}
                          className="hidden"
                          data-testid="insurance-cert-upload"
                        />
                        <div className="text-center">
                          <Shield className="w-8 h-8 text-slate-400 mx-auto mb-1" />
                          <span className="text-sm text-slate-500">Upload Insurance Cert (Image/PDF)</span>
                        </div>
                      </label>
                    )}
                  </div>

                  {/* General Photos Section */}
                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Excavator Photos
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {(formData.photos || []).map((photo, index) => (
                        <div key={index} className="relative">
                          <img
                            src={photo}
                            alt={`Photo ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-sm border border-slate-200 cursor-pointer hover:border-blue-500"
                            onClick={() => openPhotoViewer(photo, `Excavator Photo ${index + 1}`)}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveGeneralPhoto(index)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <label className="flex items-center justify-center w-20 h-20 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-blue-500 hover:bg-yellow-50/50 transition-colors">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleGeneralPhotoUpload}
                          className="hidden"
                          data-testid="excavator-photo-upload"
                        />
                        <div className="text-center">
                          <Plus className="w-6 h-6 text-slate-400 mx-auto" />
                        </div>
                      </label>
                    </div>
                    <p className="text-xs text-slate-400">Add photos of the excavator (auto-compressed)</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-semibold uppercase text-xs tracking-wide">Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes about this unit..."
                      className="rounded-sm"
                      data-testid="excavator-notes"
                    />
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-sm">
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-blue-500 text-black font-bold uppercase hover:bg-blue-600 rounded-sm"
                      data-testid="excavator-submit"
                    >
                      {editingExcavator ? "Update" : "Add"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search excavators..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-sm"
          data-testid="excavators-search"
        />
      </div>

      {/* Excavators Grid */}
      {filteredExcavators.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Truck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No excavators found</p>
            {canManage && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="mt-4 bg-blue-500 text-black font-bold uppercase hover:bg-blue-600 rounded-sm"
              >
                Add First Excavator
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredExcavators.map((excavator) => (
            <Card key={excavator.id} className="border-slate-200 hover:border-slate-400 transition-colors" data-testid={`excavator-card-${excavator.id}`}>
              <CardContent className="p-4">
                {/* Action buttons - absolute positioned */}
                {canManage && (
                  <div className="flex gap-1 float-right ml-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(excavator)}
                      className="h-7 w-7 text-slate-400 hover:text-slate-900"
                      data-testid={`edit-excavator-${excavator.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    {user?.role === "admin" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-slate-400 hover:text-red-600"
                            data-testid={`delete-excavator-${excavator.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Excavator?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete {excavator.model}. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(excavator.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                )}
                
                <div className="flex gap-4">
                  {/* Excavator Photo */}
                  {excavator.photos && excavator.photos.length > 0 ? (
                    <div 
                      className="w-20 h-20 flex-shrink-0 cursor-pointer"
                      onClick={() => openPhotoViewer(excavator.photos[0], `${excavator.model} Photo`)}
                    >
                      <img
                        src={excavator.photos[0]}
                        alt={excavator.model}
                        className="w-full h-full object-cover rounded-sm border border-slate-200 hover:border-blue-500 transition-colors"
                      />
                    </div>
                  ) : (
                    <div className="w-20 h-20 flex-shrink-0 bg-slate-100 rounded-sm flex items-center justify-center border border-slate-200">
                      <Truck className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-lg text-slate-900 pr-16">{excavator.model}</h3>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {excavator.serial_number}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
                      {excavator.year && (
                        <div className="flex items-center gap-1 text-slate-600">
                          <Calendar className="w-3 h-3" />
                          <span>{excavator.year}</span>
                        </div>
                      )}
                      {excavator.customer_name && (
                        <div className="flex items-center gap-1 text-slate-600">
                          <User className="w-3 h-3" />
                          <span className="truncate max-w-[150px]">{excavator.customer_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                  
                {/* Certificate Badges */}
                {(excavator.lm_cert_photo || excavator.insurance_cert_photo) && (
                  <div className="flex gap-2 pt-3 mt-3 border-t border-slate-100">
                    {excavator.lm_cert_photo && (
                      <button
                        onClick={() => openPhotoViewer(excavator.lm_cert_photo, "LM Certificate")}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-sm hover:bg-blue-200 transition-colors"
                        data-testid={`view-lm-cert-${excavator.id}`}
                      >
                        <FileText className="w-3 h-3" />
                        LM Cert
                        <Eye className="w-3 h-3" />
                      </button>
                    )}
                    {excavator.insurance_cert_photo && (
                      <button
                        onClick={() => openPhotoViewer(excavator.insurance_cert_photo, "Insurance Certificate")}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-sm hover:bg-green-200 transition-colors"
                        data-testid={`view-insurance-cert-${excavator.id}`}
                      >
                        <Shield className="w-3 h-3" />
                        Insurance
                        <Eye className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}

                {excavator.notes && (
                  <p className="text-slate-500 mt-3 pt-3 border-t border-slate-100 line-clamp-2 text-sm">
                    {excavator.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Photo Viewer Dialog */}
      <Dialog open={viewPhotoDialog.open} onOpenChange={(open) => setViewPhotoDialog({ ...viewPhotoDialog, open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">{viewPhotoDialog.title}</DialogTitle>
          </DialogHeader>
          {viewPhotoDialog.photo && (
            <img
              src={viewPhotoDialog.photo}
              alt={viewPhotoDialog.title}
              className="w-full h-auto max-h-[70vh] object-contain rounded-sm"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Excavators;
